import type { CustomObjectField } from '@kizen/api/custom-object';
import type { MetaConfig, PermissionValue } from '@kizen/permissions/types';
import type { ExpandState } from '__components/Containers/Expander';
import type { CustomObjectSectionData } from './types';

import { usePermissionsContext } from '@kizen/permissions/hooks/context';
import { useDisableSectionPermissions } from '@kizen/permissions/hooks/useDisableSectionPermissions';
import { usePermissionValue } from '@kizen/permissions/hooks/usePermissionValue';
import { useUpdatePermissionValues } from '@kizen/permissions/hooks/useUpdatePermissionValues';
import { getStreamKey } from '@kizen/permissions/utils';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { isFirstNameField, isLastNameField } from 'checks/fields';
import { allClientFieldsQuery, clientCategoriesQuery } from 'query/client';
import { snakeToCamelCaseKeys } from '__services/helpers';
import {
  CategoryLabel,
  FieldPermissionRows,
  PermissionRow,
  Section,
  SectionContent,
  SetAllRow,
} from './components';

type Field = SnakeToCamelCase<CustomObjectField>

type RowWithRuleTooltipProps = {
  affordance: 'checkbox' | 'range';
  label: string;
  labelIndent?: number;
  permission: string;
  section: string;
  tooltip?: string;
};

type ContactsSectionProps = {
  showArrowBtn?: boolean;
  initialExpanderState?: ExpandState;
  showSwitchToggle?: boolean;
  disableMargin?: boolean;
};

const DEFAULT_FIELDS = 'default_fields';
const defaultFieldsOrder: Record<string, number> = {
  email: 0,
  created: 1,
  first_name: 2,
  email_status: 3,
  mobile_phone: 4,
  business_phone: 5,
  home_phone: 6,
  tags: 7,
  titles: 8,
  birthday: 9,
  timezone: 10,
};

export const CONTACTS_SECTION_KEY = 'contacts_section';

const withoutLastName = (field: Field) => !isLastNameField(field);

const RowWithRuleTooltip = ({
  affordance,
  label,
  labelIndent,
  permission,
  section,
  tooltip,
}: RowWithRuleTooltipProps) => {
  const ruleTooltip = usePermissionValue(
    getStreamKey(permission, section),
    ({ tooltip }) => tooltip
  );

  return (
    <PermissionRow
      section={section}
      permission={permission}
      affordance={affordance}
      tooltip={ruleTooltip || tooltip}
      label={label}
      labelIndent={labelIndent}
    />
  );
};

export const ContactsSection = ({
  showArrowBtn,
  initialExpanderState,
  showSwitchToggle,
  disableMargin,
}: ContactsSectionProps) => {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const ctx = usePermissionsContext();
  const { contact_section, existing_contact_values, meta_config } = ctx;
  const {
    contacts,
    default_contact_fields,
    contact_categories,
    default_access_labels,
  } = meta_config;
  const { label, key } = contact_section;
  const previousCategoryHadLabels = useRef(false);
  const isSectionEnabled = usePermissionValue(key, ({ value }) => {
    return Boolean(value);
  });
  const initialValue = usePermissionValue(key, ({ initial }) => {
    return initial?.value;
  });

  const sectionLabels = contact_section.access_labels ?? default_access_labels;

  const { data: categories = [] } = useQuery(
    clientCategoriesQuery({
      enabled: isSectionEnabled,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    })
  );

  const { data: fields = [] } = useQuery(
    allClientFieldsQuery({
      enabled: isSectionEnabled,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      select: (results) => {
        return snakeToCamelCaseKeys(results);
      },
    })
  );

  const updatePermissionValues = useUpdatePermissionValues();
  const disableSectionPermissions = useDisableSectionPermissions();

  const filteredFields = useMemo<Field[]>(() => {
    return fields?.filter(withoutLastName) ?? [];
  }, [fields]);

  const categorizedFields = useMemo(() => {
    if (!filteredFields?.length || !categories) return [];
    const categorizedFields = filteredFields.reduce<Record<string, Field[]>>(
      (acc, field) => {
        const category = field.isDefault ? DEFAULT_FIELDS : field.category;
        const updatedField = isFirstNameField(field)
          ? { ...field, displayName: t('Full Name') }
          : field;
        acc[category] = acc[category] || [];
        acc[category].push(updatedField);
        return acc;
      },
      {}
    );
    const defaultFieldsCategory = {
      id: DEFAULT_FIELDS,
      name: t('Default Fields'),
      fields: categorizedFields[DEFAULT_FIELDS].sort((a, b) => {
        return defaultFieldsOrder[a.name] - defaultFieldsOrder[b.name];
      }),
    };
    return [defaultFieldsCategory].concat(
      categories
        .map((category) => ({
          ...category,
          fields: categorizedFields[category.id] ?? [],
        }))
        .sort((a, b) => {
          return a.order! - b.order!;
        })
    );
  }, [filteredFields, categories, t]);

  const allFieldPermissionKeys = useMemo(() => {
    return fields?.map(({ name }) => getStreamKey(name, key)) ?? [];
  }, [key, fields]);

  const { main, bulk, bulk_keys, default_for_new_field } = useMemo(() => {
    if (!isSectionEnabled) {
      return { main: [], bulk: [], bulk_keys: [], default_for_new_field: null };
    }

    return contacts.reduce<CustomObjectSectionData>(
      (acc, permission) => {
        if (permission.key === 'default_for_new_field') {
          acc.default_for_new_field = permission;
        } else if (permission.is_bulk_action) {
          acc.bulk.push(permission);
          acc.bulk_keys.push(getStreamKey(permission.key, key));
        } else {
          if (
            permission.category &&
            !acc.main.find(({ key }) => key === permission.category)
          ) {
            const contactCategory = contact_categories.find(
              ({ key }) => key === permission.category
            );

            if (contactCategory) {
              acc.main.push({ ...contactCategory, isCategory: true });
            }
          }
          acc.main.push(permission);
        }
        return acc;
      },
      { main: [], bulk: [], bulk_keys: [], default_for_new_field: null }
    );
  }, [isSectionEnabled, contacts, contact_categories, key]);

  const handleSectionToggle = useCallback(
    async (value: boolean) => {
      if (value) {
        if (initialValue && existing_contact_values) {
          updatePermissionValues(existing_contact_values);
        } else {
          const data = await qc.fetchQuery(allClientFieldsQuery());
          const fields = snakeToCamelCaseKeys(data);
          const fieldValues = fields
            .filter(withoutLastName)
            .reduce<Record<string, PermissionValue>>((acc, f) => {
              const key = `${CONTACTS_SECTION_KEY}__${f.name}`;

              if (default_contact_fields && f.name in default_contact_fields) {
                const name =
                  f.name as keyof MetaConfig['default_contact_fields'];
                acc[key] = default_contact_fields[name].default;
              } else {
                acc[key] = 'view';
              }

              return acc;
            }, {});
          const permissionValues = contacts.reduce<
            Record<string, PermissionValue>
          >((acc, config) => {
            const key = getStreamKey(config.key, CONTACTS_SECTION_KEY);
            acc[key] = config.default;
            return acc;
          }, {});
          updatePermissionValues({ ...fieldValues, ...permissionValues });
        }
      } else {
        disableSectionPermissions(CONTACTS_SECTION_KEY);
      }
    },
    [
      initialValue,
      existing_contact_values,
      default_contact_fields,
      contacts,
      updatePermissionValues,
      disableSectionPermissions,
      qc,
    ]
  );

  return (
    <Section
      title={label}
      sectionId={key}
      onChange={handleSectionToggle}
      showArrowBtn={showArrowBtn}
      initialExpanderState={initialExpanderState}
      showSwitchToggle={showSwitchToggle}
      disableMargin={disableMargin}
    >
      {isSectionEnabled && (
        <SectionContent labels={sectionLabels}>
          {main.map((data) => {
            if ('isCategory' in data) {
              const {
                key: permission_key,
                label,
                tooltip,
                access_labels,
              } = data;

              let categoryLabels = access_labels;

              if (previousCategoryHadLabels.current && !categoryLabels) {
                categoryLabels = sectionLabels;
              }

              previousCategoryHadLabels.current = Boolean(categoryLabels);

              return (
                <CategoryLabel
                  key={permission_key}
                  marginBottom={label ? null : "17"}
                  accessLabels={categoryLabels}
                  label={label}
                  tooltip={tooltip}
                />
              );
            }

            const {
              affordance,
              key: permission_key,
              label,
              tooltip,
              category,
            } = data;

            return (
              <RowWithRuleTooltip
                key={permission_key}
                section={key}
                permission={permission_key}
                affordance={affordance}
                tooltip={tooltip}
                label={label}
                labelIndent={category ? 40 : 0}
              />
            );
          })}
          <SetAllRow
            showNone
            showRemove
            label={t('Perform Bulk Actions (Set All)')}
            permissionKeys={bulk_keys}
          />
          {bulk.map(({ affordance, key: permission_key, label, tooltip }) => (
            <RowWithRuleTooltip
              key={key}
              section={key}
              permission={permission_key}
              affordance={affordance}
              label={label}
              tooltip={tooltip}
              labelIndent={40}
            />
          ))}
          {default_for_new_field && (
            <RowWithRuleTooltip
              key={default_for_new_field.key}
              section={key}
              permission={default_for_new_field.key}
              affordance={default_for_new_field.affordance}
              label={default_for_new_field.label}
              tooltip={default_for_new_field.tooltip}
            />
          )}
          <SetAllRow
            showNone
            showView
            showEdit
            label={t('Individual Contact Field Permission (Set All)')}
            permissionKeys={allFieldPermissionKeys}
          />
          {categorizedFields.map(({ id, name, fields }) => (
            <FieldPermissionRows
              key={id}
              sectionId={key}
              categoryName={name}
              fields={fields}
            />
          ))}
        </SectionContent>
      )}
    </Section>
  );
};
